import { routes } from '@/lib/routes';
import { motion } from 'framer-motion';
import { Calendar, PenLine, Send, Stamp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

interface EnvelopeSuccessProps {
  senderName: string;
  recipientName: string;
  recipientCountry: string;
  deliveryDate: string;
  onWriteAnother: () => void;
}

export function EnvelopeSuccess({
  senderName,
  recipientName,
  recipientCountry,
  deliveryDate,
  onWriteAnother
}: EnvelopeSuccessProps) {
  const { t } = useTranslation();

  return (
    <div className="text-center py-8">
      {/* Success Message */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-serif text-ink mb-2">{t('compose.sent.title')}</h1>
        <p className="text-sepia/70">
          {t('compose.sent.description', { recipient: recipientName })}
        </p>
      </motion.div>

      {/* Envelope Visualization */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9, rotateX: 20 }}
        animate={{ opacity: 1, scale: 1, rotateX: 0 }}
        transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
        className="max-w-xl mx-auto mb-8"
        style={{ perspective: '1000px' }}
      >
        {/* Envelope Container */}
        <div
          className="relative bg-cream rounded-lg overflow-hidden"
          style={{
            boxShadow: '0 20px 60px rgba(91, 75, 61, 0.2), 0 8px 20px rgba(91, 75, 61, 0.1)',
            border: '2px solid rgba(197, 165, 114, 0.3)'
          }}
        >
          {/* Envelope Paper Texture */}
          <div className="paper-texture absolute inset-0 pointer-events-none opacity-40" />

          {/* Envelope Flap (decorative top) */}
          <div
            className="absolute top-0 left-0 right-0 h-20"
            style={{
              background:
                'linear-gradient(to bottom, rgba(197, 165, 114, 0.2) 0%, transparent 100%)'
            }}
          />

          {/* Envelope Content */}
          <div className="relative p-10 min-h-[320px]">
            {/* Stamp in top-right corner */}
            <motion.div
              initial={{ rotate: -10, scale: 0 }}
              animate={{ rotate: 0, scale: 1 }}
              transition={{ duration: 0.3, delay: 0.6, type: 'spring' }}
              className="absolute top-6 right-6"
            >
              <div
                className="w-18 h-22 rounded border-2 border-gold/50 bg-gradient-to-br from-gold/10 to-gold/20 flex flex-col items-center justify-center px-3 py-4"
                style={{
                  boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)'
                }}
              >
                <Stamp className="w-7 h-7 text-gold mb-1" />
                <span className="text-[9px] uppercase tracking-wider text-gold/70">
                  {t('compose.sent.stamp')}
                </span>
              </div>
            </motion.div>

            {/* From Address - top left */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.4 }}
              className="text-left"
            >
              <p className="text-[10px] uppercase tracking-widest text-sepia/50 mb-1">
                {t('compose.sent.from')}
              </p>
              <p className="text-base font-medium text-sepia">{senderName}</p>
            </motion.div>

            {/* To Address - bottom right */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.5 }}
              className="absolute bottom-24 right-10 text-right"
            >
              <p className="text-[10px] uppercase tracking-widest text-sepia/50 mb-1">
                {t('compose.sent.to')}
              </p>
              <p className="text-2xl font-serif text-ink mb-1">{recipientName}</p>
              <p className="text-sm text-sepia/70">{recipientCountry}</p>
            </motion.div>

            {/* Delivery Date - bottom left */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.6 }}
              className="absolute bottom-10 left-10 flex items-center gap-2 text-blue"
            >
              <Calendar className="w-4 h-4" />
              <div>
                <p className="text-[10px] uppercase tracking-widest text-sepia/50">
                  {t('compose.sent.arriving')}
                </p>
                <p className="text-sm font-medium">{deliveryDate}</p>
              </div>
            </motion.div>
          </div>

          {/* Bottom decorative edge */}
          <div
            className="h-2"
            style={{
              background:
                'linear-gradient(to right, rgba(197, 165, 114, 0.2), rgba(197, 165, 114, 0.4), rgba(197, 165, 114, 0.2))'
            }}
          />
        </div>
      </motion.div>

      {/* Action Buttons */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.9 }}
        className="flex flex-col sm:flex-row gap-3 justify-center"
      >
        <button
          type="button"
          onClick={onWriteAnother}
          className="flex items-center justify-center gap-2 px-6 py-2.5 bg-blue text-white rounded-lg font-medium hover:bg-blue/90 transition-all"
        >
          <PenLine className="w-4 h-4" />
          {t('compose.sent.writeAnother')}
        </button>
        <Link
          to={routes.sent}
          className="flex items-center justify-center gap-2 px-6 py-2.5 border border-gray-warm text-sepia rounded-lg font-medium hover:bg-gray-warm/20 transition-all"
        >
          <Send className="w-4 h-4" />
          {t('compose.sent.viewSent')}
        </Link>
      </motion.div>
    </div>
  );
}
